import Dexie, { type EntityTable, type Table } from 'dexie';
import type {
  ChildFeedback,
  ExerciseAttempt,
  LearnerAchievement,
  LearnerProfile,
  LearningSession,
  MasteryRecord,
} from '@/domain/records';
import { applyMigrations } from './migrations';

export interface MetaEntry {
  key: string;
  value: unknown;
}

/** Local-first storage. Everything stays on this device unless the parent exports it. */
export class AppDatabase extends Dexie {
  declare learners: EntityTable<LearnerProfile, 'id'>;
  declare sessions: EntityTable<LearningSession, 'id'>;
  declare attempts: EntityTable<ExerciseAttempt, 'id'>;
  declare mastery: Table<MasteryRecord, [string, string]>;
  declare achievements: Table<LearnerAchievement, [string, string]>;
  declare meta: Table<MetaEntry, string>;
  declare feedback: EntityTable<ChildFeedback, 'id'>;

  constructor(name = 'obigo') {
    super(name);
    applyMigrations(this);
  }
}

export const db = new AppDatabase();

export type StorageProblem = 'unavailable' | 'quota' | 'blocked' | 'unknown';

export class StorageError extends Error {
  readonly problem: StorageProblem;
  constructor(problem: StorageProblem, cause?: unknown) {
    super(`Local storage problem: ${problem}`, { cause });
    this.name = 'StorageError';
    this.problem = problem;
  }
}

export function toStorageError(error: unknown): StorageError {
  if (error instanceof StorageError) return error;
  const name = (error as { name?: string } | undefined)?.name ?? '';
  const inner = (error as { inner?: { name?: string } } | undefined)?.inner?.name ?? '';
  if (name === 'QuotaExceededError' || inner === 'QuotaExceededError')
    return new StorageError('quota', error);
  if (name === 'MissingAPIError' || name === 'InvalidStateError')
    return new StorageError('unavailable', error);
  if (name === 'DatabaseClosedError' || name === 'VersionError' || name === 'BlockedError')
    return new StorageError('blocked', error);
  return new StorageError('unknown', error);
}

/** Opens the database, translating low-level failures into a StorageError. */
export async function openDatabase(database: AppDatabase = db): Promise<void> {
  try {
    if (typeof indexedDB === 'undefined') throw new StorageError('unavailable');
    await database.open();
  } catch (error) {
    throw toStorageError(error);
  }
}

/** Runs a write and normalises errors so the UI can explain them. */
export async function safeWrite<T>(operation: () => Promise<T>): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    throw toStorageError(error);
  }
}
